/**
 * CalendarManager.ts
 *
 * 캘린더 자체의 CRUD 작업을 담당하는 모듈
 *
 * 주요 기능:
 * - 캘린더 생성, 수정, 삭제
 * - 캘린더 정보 조회 및 설정
 * - 캘린더 공유 및 초대 관리
 * - 캘린더 색상 및 이름 변경
 * - 뷰 타입 설정 관리
 *
 * 이 모듈은 캘린더 자체의 생명주기를 관리하며,
 * 사용자가 캘린더를 생성하고 설정을 변경할 수 있도록 합니다.
 */

import settings from 'popups/Settings'

import { classInfo, folderEmitter, instanceEmitter, getUserInfo } from './CalendarState'
import { getClient } from './CalendarClient'
import { fetchCalendars } from './CalendarSync'
// 순환 참조 방지를 위해 EventManager 대신 공유 유틸리티에서 import
import { isPersonaEmail } from 'utils/personaEmail'
import * as eventInstanceManager from 'pages/calendar/modules/EventInstanceManager'

/**
 * Calendar view types
 */
export type CalendarViewType = 'month' | 'week' | 'day' | 'agenda'

/**
 * DAV response structure
 */
interface DavResponse {
  ok?: boolean
  href?: string
  [key: string]: unknown
}

/**
 * Create a new calendar
 *
 * @param name - Calendar display name
 * @param color - Calendar color (hex format)
 * @param completeCallback - Callback with success status
 */
export const createCalendar = (
  name: string,
  color: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  let xml = '<?xml version="1.0" encoding="utf-8" ?>'
  xml +=
    '<D:mkcol xmlns:D="DAV:" xmlns:CAL="urn:ietf:params:xml:ns:caldav" xmlns:CARD="urn:ietf:params:xml:ns:carddav" xmlns:n0="http://apple.com/ns/ical/">'
  xml += '<D:set>'
  xml += '<D:prop>'
  xml += '<D:resourcetype>'
  xml += '<D:collection />'
  xml += '<CAL:calendar />'
  xml += '</D:resourcetype>'
  xml += '<D:displayname>' + name + '</D:displayname>'
  xml += '<n0:calendar-color>' + color + '</n0:calendar-color>'
  xml += '<CAL:supported-calendar-component-set>'
  xml += '<CAL:comp name="VEVENT" />'
  xml += '</CAL:supported-calendar-component-set>'
  xml += '</D:prop>'
  xml += '</D:set>'
  xml += '</D:mkcol>'

  const newId = crypto.randomUUID()
  const newUrl = client.account.homeUrl + newId + '/'

  client
    .davRequest({
      url: newUrl,
      convertIncoming: false,
      init: {
        method: 'MKCOL',
        body: xml,
        headers: {
          'Content-Type': 'application/xml; charset="utf-8"',
          Depth: '0',
        },
      },
    })
    .then((response) => {
      console.log('create calendar success %o', response)

      const calendarSettings = settings.getValue('calendar')
      const newCalendar = {
        ...calendarSettings,
        calendarSyncTargets: [...calendarSettings.calendarSyncTargets, newUrl],
      }
      settings
        .setValue('calendar', newCalendar)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { calendar: newCalendar },
          })
        })
        .catch((err) => console.error('Failed to sync calendar settings:', err))
      fetchCalendars()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('create calendar failed : %o', error)
      completeCallback(false)
    })
}

/**
 * Update calendar properties (name, color)
 *
 * @param calendarUrl - Calendar URL
 * @param name - New calendar name
 * @param color - New calendar color
 * @param completeCallback - Callback with success status
 */
export const updateCalendar = (
  calendarUrl: string,
  name: string,
  color: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  const oldCalendar = classInfo.calendars?.find((c) => c.url === calendarUrl)
  if (oldCalendar) {
    const newCalendar = { ...oldCalendar, displayName: name, calendarColor: color }
    classInfo.calendars = classInfo.calendars!.map((c) => {
      if (c.url === calendarUrl) {
        return newCalendar
      }
      return c
    })
    if (oldCalendar.color !== color) {
      classInfo.instances.forEach((instance) => {
        if (instance.serverId.startsWith(calendarUrl)) {
          instance.changeFolderColor(color)
        }
      })
    }
    folderEmitter.emit('folder')
  }

  let xml = '<?xml version="1.0" encoding="utf-8" ?>'
  xml += '<D:propertyupdate xmlns:D="DAV:" xmlns:A="http://apple.com/ns/ical/">'
  xml += '<D:set>'
  xml += '<D:prop>'
  xml += '<D:displayname>' + name + '</D:displayname>'
  xml += '<A:calendar-color>' + color + '</A:calendar-color>'
  xml += '</D:prop>'
  xml += '</D:set></D:propertyupdate>'
  client
    .davRequest({
      url: calendarUrl,
      convertIncoming: false,
      init: {
        method: 'PROPPATCH',
        body: xml,
      },
    })
    .then((response) => {
      console.log('update calendar success %o', response)
      fetchCalendars()
      completeCallback(true)

      const oldColor = oldCalendar?.calendarColor._cdata
      if (oldColor !== color) {
        classInfo.instances.forEach((i) => {
          if (
            i.serverId.startsWith(calendarUrl) &&
            i.color === oldColor &&
            !(i.event as any)?.color
          ) {
            ;(i as any).color = color
          }
        })
        instanceEmitter.emit('instance')
      }
    })
    .catch((error) => {
      console.log('update calendar failed : %o', error)
      if (oldCalendar) {
        classInfo.calendars = classInfo.calendars!.map((c) => {
          if (c.url === calendarUrl) {
            return oldCalendar
          }
          return c
        })
        folderEmitter.emit('folder')
      }
      completeCallback(false)
    })
}

/**
 * Delete a calendar
 *
 * @param calendarUrl - Calendar URL
 * @param completeCallback - Callback with success status
 */
export const deleteCalendar = (
  calendarUrl: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  let oldCalendar: any = null
  let oldCalendarIndex = -1
  classInfo.calendars = classInfo.calendars!.filter((c, index) => {
    if (c.url === calendarUrl) {
      oldCalendar = c
      oldCalendarIndex = index
      return false
    }
    return true
  })
  folderEmitter.emit('folder')

  client
    .davRequest({
      url: calendarUrl,
      convertIncoming: false,
      init: {
        method: 'DELETE',
        body: null,
      },
    })
    .then((response) => {
      console.log('delete calendar success %o', response)
      const calendarSettings = settings.getValue('calendar')
      const remainSyncTargets = calendarSettings.calendarSyncTargets.filter(
        (t: string) => t !== calendarUrl,
      )
      const newCalendar = {
        ...calendarSettings,
        calendarSyncTargets: remainSyncTargets,
      }
      settings
        .setValue('calendar', newCalendar)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { calendar: newCalendar },
          })
        })
        .catch((err) => console.error('Failed to sync calendar settings:', err))
      eventInstanceManager.removeInstanceByCalendar(calendarUrl)
      fetchCalendars()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('delete calendar failed : %o', error)
      if (oldCalendarIndex !== -1 && oldCalendar) {
        classInfo.calendars!.splice(oldCalendarIndex, 0, oldCalendar)
        folderEmitter.emit('folder')
      }
      completeCallback(false)
    })
}

/**
 * Get calendar information including sharing details
 *
 * @param folderUrl - Calendar URL
 * @param completeCallback - Callback with calendar info or null
 */
export const getCalendarInfo = (
  folderUrl: string,
  completeCallback: (result: DavResponse | null) => void = (_result) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(null)
    return
  }

  let xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<D:propfind xmlns:D="DAV:" xmlns:CAL="urn:ietf:params:xml:ns:caldav" xmlns:n0="http://apple.com/ns/ical/" xmlns:S="http://calendarserver.org/ns/">' +
    '<D:prop>' +
    '<S:invite>' +
    '<S:organizer/><S:invitee/><S:access/><S:invite-noresponse/>' +
    '</S:invite>' +
    '<D:resourcetype/><D:displayname/><n0:calendar-color/><CAL:calendar-description/>' +
    '<D:calendar-timezone/><CAL:supported-calendar-component-set/><S:source/><D:owner/>' +
    '</D:prop>' +
    '</D:propfind>'

  client
    .davRequest({
      url: folderUrl,
      convertIncoming: false,
      init: {
        method: 'PROPFIND',
        body: xml,
        headers: {
          Depth: '0',
        },
      },
    })
    .then((response: DavResponse[]) => {
      console.log('get calendar info success %o', response)
      completeCallback(response[0])
    })
    .catch((error) => {
      console.log('get calendar info failed : %o', error)
      completeCallback(null)
    })
}

/**
 * Send calendar invite to another user
 *
 * @param calendarUrl - Calendar URL
 * @param calendarName - Calendar name
 * @param toEmail - Recipient email
 * @param isReadOnly - Whether to grant read-only access
 * @param completeCallback - Callback with success status
 */
export const sendInviteCalendar = (
  calendarUrl: string,
  calendarName: string,
  toEmail: string,
  isReadOnly: boolean,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  const userInfo = getUserInfo()

  const realEmail = userInfo.personaEmail
  if (!!realEmail && realEmail !== toEmail && !isPersonaEmail(toEmail)) {
    const domain = userInfo.personaDomain
    const toEmailParts = toEmail.split('@')
    toEmail = toEmailParts.join('_at_') + '@' + domain
  }

  const xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<D:share-resource xmlns:D="DAV:">' +
    '<D:sharee>' +
    `<D:href>mailto:${toEmail}</D:href>` +
    '<D:prop>' +
    `<D:displayname>${calendarName}</D:displayname>` +
    '</D:prop>' +
    `<D:comment>${calendarName}</D:comment>` +
    `<D:share-access><D:${isReadOnly ? 'read' : 'read-write'}/></D:share-access>` +
    '</D:sharee>' +
    '</D:share-resource>'

  client
    .davRequest({
      url: calendarUrl,
      convertIncoming: false,
      init: {
        method: 'POST',
        body: xml,
      },
    })
    .then((response) => {
      console.log('invite calendar success %o', response)
      fetchCalendars()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('invite calendar failed : %o', error)
      completeCallback(false)
    })
}

/**
 * Remove calendar invite for a user
 *
 * @param calendarUrl - Calendar URL
 * @param toEmail - User email to remove access from
 * @param completeCallback - Callback with success status
 */
export const deleteInviteCalendar = (
  calendarUrl: string,
  toEmail: string,
  completeCallback: (success: boolean) => void = (_success) => {},
): void => {
  const client = getClient()
  if (!client) {
    completeCallback(false)
    return
  }

  const xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<D:share-resource xmlns:D="DAV:"><D:sharee>' +
    `<D:href>mailto:${toEmail}</D:href>` +
    '<D:share-access><D:no-access /></D:share-access>' +
    '</D:sharee></D:share-resource>'

  client
    .davRequest({
      url: calendarUrl,
      convertIncoming: false,
      init: {
        method: 'POST',
        body: xml,
      },
    })
    .then((response) => {
      console.log('delete invite calendar success %o', response)
      fetchCalendars()
      completeCallback(true)
    })
    .catch((error) => {
      console.log('delete invite calendar failed : %o', error)
      completeCallback(false)
    })
}

/**
 * Set the calendar view type in settings
 *
 * @param viewType - View type to set
 */
export const setViewType = (viewType: CalendarViewType): void => {
  const localSettings = settings.getValue('localSettings')
  settings.setValue('localSettings', {
    ...localSettings,
    calendar: {
      ...localSettings.calendar,
      calendarViewType: viewType,
    },
  })
}
